/**
 * SearchTermsPanel Component
 *
 * Displays search term performance data for a campaign.
 * RMF Compliance: Search Term View with search terms and match types.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useAuthStore } from '@/store/authStore';
import { getSearchTerms, type SearchTermItem } from '@/services/api/searchTerms';
import { Button, LoadingSpinner } from '@/components/ui';
import type { Campaign } from '@/types/campaign';
import {
  formatNumberDisplay as formatNumber,
  formatCurrencyDisplay as formatCurrency,
  formatPercentDisplay as formatPercent,
} from '../utils';

const ORGANIC_IMPR_TOOLTIP = "Shows organic impressions if the paid search term also appears in GSC. Not all paid search terms will have organic impressions.";
const ORGANIC_CLICKS_TOOLTIP = "Shows organic clicks if the paid search term also appears in GSC. Not all paid search terms will have organic clicks.";

interface SearchTermsPanelProps {
  campaign: Campaign;
  hasGscData?: boolean;
}

export function SearchTermsPanel({ campaign, hasGscData = false }: SearchTermsPanelProps) {
  const { memberUuid } = useAuthStore();
  const [searchTerms, setSearchTerms] = useState<SearchTermItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [organicData, setOrganicData] = useState<Map<string, { clicks: number; impressions: number }>>(new Map());
  const [isLoadingOrganic, setIsLoadingOrganic] = useState(false);

  const loadSearchTerms = useCallback(async () => {
    if (!memberUuid) {
      setError('Authentication required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getSearchTerms({
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        days: 28,
        limit: 100,
      });

      setSearchTerms(response.search_terms);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading search terms:', err);
      setError(err instanceof Error ? err.message : 'Failed to load search terms');
    } finally {
      setIsLoading(false);
    }
  }, [memberUuid, campaign.campaign_id]);

  // Reset state when campaign changes
  useEffect(() => {
    setHasLoaded(false);
    setSearchTerms([]);
    setError(null);
  }, [campaign.campaign_id]);

  // Auto-load on mount and when campaign changes
  useEffect(() => {
    if (!hasLoaded && memberUuid) {
      loadSearchTerms();
    }
  }, [hasLoaded, memberUuid, loadSearchTerms]);

  // Fetch organic search term data when GSC is available and search terms are loaded
  // Re-fetches when campaign changes to get organic data for the new campaign
  // Note: hasSearchTerms (boolean) avoids re-fetching when array contents change
  // but emptiness state remains the same (e.g., length 5 -> 10 doesn't re-trigger).
  // This is intentional because the organic data fetch gets ALL organic data for the
  // campaign (up to limit), not just data matching the current searchTerms list.
  // The matching happens client-side via the organicData Map lookup.
  const hasSearchTerms = searchTerms.length > 0;
  useEffect(() => {
    // Clear stale data when campaign changes to avoid showing old campaign's organic data
    setOrganicData(new Map());

    if (!hasGscData || !hasSearchTerms) return;

    // Track if this effect has been cancelled (user switched campaigns)
    let cancelled = false;

    const fetchOrganicData = async () => {
      setIsLoadingOrganic(true);
      try {
        const { getSearchTerms: getGscSearchTerms } = await import('@/services/api/gsc');
        // Filter by campaign_id to get organic data relevant to this campaign's search terms
        const data = await getGscSearchTerms({
          campaign_id: campaign.campaign_id,
          limit: 500,
          days: 28
        });

        // Don't update state if campaign changed while request was in flight
        if (cancelled) return;

        // Build a map of search_term -> { clicks, impressions }
        const organicMap = new Map<string, { clicks: number; impressions: number }>();
        for (const term of data) {
          // Aggregate organic metrics by search term (case-insensitive key)
          const key = term.search_term.toLowerCase();
          const existing = organicMap.get(key) || { clicks: 0, impressions: 0 };
          organicMap.set(key, {
            clicks: existing.clicks + term.organic_clicks,
            impressions: existing.impressions + term.organic_impressions,
          });
        }
        setOrganicData(organicMap);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch organic search term data:', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingOrganic(false);
        }
      }
    };

    fetchOrganicData();

    // Cleanup: mark as cancelled when effect re-runs or component unmounts
    return () => {
      cancelled = true;
    };
  }, [hasGscData, hasSearchTerms, campaign.campaign_id]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading search terms...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={loadSearchTerms}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (searchTerms.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No search term data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Search terms are collected when your Search campaign receives traffic
          </p>
        </div>
      </div>
    );
  }

  // Show "Top 100" indicator when we have exactly 100 results (likely more exist)
  const showTop100Indicator = searchTerms.length === 100;

  // Show organic column when GSC is available (we'll fetch the data)
  const showOrganicColumn = hasGscData;

  // Add vertical scroll when more than 10 search terms
  const needsScroll = searchTerms.length > 10;

  return (
    <div className={`overflow-x-auto ${needsScroll ? 'max-h-[480px] overflow-y-auto' : ''}`}>
      <div className="flex justify-between items-center mb-2">
        {showTop100Indicator ? (
          <p className="text-xs text-gray-500">Top 100 Search Terms by Cost</p>
        ) : (
          <span></span>
        )}
        <p className="text-xs text-gray-500">28-Day Totals</p>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${needsScroll ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-400 w-8">
              #
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Search Term
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Match Type
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Keyword
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Impr
            </th>
            {showOrganicColumn && (
              <th className="pl-3 pr-6 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                <span className="group relative inline-flex items-center justify-end">
                  <span>Org Impr</span>
                  <button
                    type="button"
                    className="ml-1 -mr-5 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    aria-label="Organic impressions information"
                  >
                    <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                  </button>
                  <span className="invisible group-hover:visible group-focus-within:visible absolute top-full right-0 mt-1 w-[304px] p-2 text-xs font-normal normal-case text-left text-white bg-gray-800 rounded shadow-lg z-10">
                    {ORGANIC_IMPR_TOOLTIP}
                  </span>
                </span>
              </th>
            )}
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Clicks
            </th>
            {showOrganicColumn && (
              <th className="pl-3 pr-6 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                <span className="group relative inline-flex items-center justify-end">
                  <span>Org Clicks</span>
                  <button
                    type="button"
                    className="ml-1 -mr-5 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    aria-label="Organic clicks information"
                  >
                    <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                  </button>
                  <span className="invisible group-hover:visible group-focus-within:visible absolute top-full right-0 mt-1 w-[304px] p-2 text-xs font-normal normal-case text-left text-white bg-gray-800 rounded shadow-lg z-10">
                    {ORGANIC_CLICKS_TOOLTIP}
                  </span>
                </span>
              </th>
            )}
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              CTR
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cost
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Conv
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {searchTerms.map((term, index) => (
            <tr key={`${term.search_term}-${term.match_type}-${term.keyword_text}-${index}`} className="hover:bg-gray-50">
              <td className="px-1.5 py-2 text-xs text-gray-400 w-8">
                {index + 1}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate" title={term.search_term}>
                {term.search_term}
              </td>
              <td className="px-3 py-2 text-sm">
                {term.match_type ? (
                  <span className="px-2 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-gray-700 whitespace-nowrap">
                    {term.match_type}
                  </span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate" title={term.keyword_text || ''}>
                {term.keyword_text || '-'}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(term.impressions)}
              </td>
              {showOrganicColumn && (
                <td className="pl-3 pr-6 py-2 text-sm text-gray-500 text-right">
                  {isLoadingOrganic ? (
                    <span className="text-gray-400">...</span>
                  ) : (
                    formatNumber(organicData.get(term.search_term.toLowerCase())?.impressions)
                  )}
                </td>
              )}
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(term.clicks)}
              </td>
              {showOrganicColumn && (
                <td className="pl-3 pr-6 py-2 text-sm text-gray-500 text-right">
                  {isLoadingOrganic ? (
                    <span className="text-gray-400">...</span>
                  ) : (
                    formatNumber(organicData.get(term.search_term.toLowerCase())?.clicks)
                  )}
                </td>
              )}
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatPercent(term.ctr)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatCurrency(term.cost)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(term.conversions)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SearchTermsPanel;
